<template>
  <div>
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ pageTitle }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LABELS.LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar">
          <router-link
            v-if="!mechanicsOnly"
            v-permission="['users-create']"
            to="add"
            class="btn btn-light-success font-weight-bolder"
            ><i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_NEW") }}</router-link
          >
        </div>
      </template>
      <v-card v-show="!isPageLoading">
        <ResponsiveFilterSheet
          :filters="{}"
          :searchQuery.sync="searchQuery"
          :show-filters="false"
          @search="doSearch"
          @clear="doClear"
        >
          <template v-slot:desktop>
            <SearchTools
              :searchQuery.sync="searchQuery"
              @do-search="doSearch"
              @do-clear="doClear"
            ></SearchTools>
          </template>
        </ResponsiveFilterSheet>
        <ResponsiveDataTable
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          :loading="isLoadingUser"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right',
          }"
          @update:options="getDataFromApi"
          :mobile-breakpoint="mechanicsOnly ? 768 : 0"
          :class="{ 'px-6': !mechanicsOnly, 'elevation-1': true }"
          :mobile-summary-fields="mobileSummaryFields"
          :mobile-detail-fields="mobileDetailFields"
          :mobile-toggle-label="$t('LABELS.DETAILS')"
        >
          <template v-slot:[`item.email`]="{ item }">
            <router-link
              v-if="canEditUsers"
              :to="{ name: 'edit-user', params: { id: item.id } }"
            >
              {{ item.email }}</router-link
            >
            <template v-else>{{ item.email }}</template>
          </template>

          <template v-slot:[`item.firstName`]="{ item }">
            <router-link
              v-if="canEditUsers"
              :to="{ name: 'edit-user', params: { id: item.id } }"
            >
              {{ item.firstName }}
            </router-link>
            <template v-else>{{ item.firstName }}</template>
          </template>
          <template v-slot:[`item.lastName`]="{ item }">
            <router-link
              v-if="canEditUsers"
              :to="{ name: 'edit-user', params: { id: item.id } }"
            >
              {{ item.lastName }}</router-link
            >
            <template v-else>{{ item.lastName }}</template>
          </template>

          <template v-slot:[`item.active`]="{ item }">
            <v-chip :color="getColor(item.active)" small dark pill>
              <v-avatar left>
                <v-icon>{{ iconIsActive(item.active) }}</v-icon>
              </v-avatar>
              {{ textIsActive(item.active) }}</v-chip
            >
          </template>
          <template v-if="!mechanicsOnly" v-slot:[`item.actions`]="{ item }">
            <router-link
              v-if="canEditUsers"
              :to="{ name: 'edit-user', params: { id: item.id } }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    class="mr-2"
                    fab
                    dark
                    x-small
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("LABELS.EDIT") }}</span>
              </v-tooltip>
            </router-link>
            <template v-else>Няма права</template>
          </template>
          <template v-slot:mobile-summary-active="{ item }">
            <span class="user-list__status d-inline-flex align-items-center">
              <span
                class="user-list__status-dot rounded-circle mr-2"
                :style="{ backgroundColor: item.active ? '#3699ff' : '#f64e60' }"
              ></span>
              {{ textIsActive(item.active) }}
            </span>
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>
        </ResponsiveDataTable>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
    </b-card>
  </div>
</template>

<script>
//General
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

import { mapGetters } from "vuex";
import FormSelect from "@/view/content/forms/components/FormSelect.vue";
import SearchTools from "@/core/components/table/SearchTools.vue";
import ResponsiveDataTable from "@/core/components/table/ResponsiveDataTable.vue";
import ResponsiveFilterSheet from "@/core/components/table/ResponsiveFilterSheet.vue";

//User Module
import { FETCH_MECHANICS, FETCH_USERS } from "@/modules/user/store/user.module";

import { permissionMixin } from "@/core/mixins/permissionMixin.js";

export default {
  mixins: [permissionMixin],
  props: {
    mechanicsOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isPageLoading: true,
      tableLoading: true,
      items: [],
      totalItems: 100,
      searchQuery: "",
      options: {
        descending: true,
        page: 1,
        itemsPerPage: 30,
        totalItems: 100,
      },
      filters: {
        roleId: null,
      },
      rowsPerPageItems: [10, 20, 30, 40, 50],
      columns: {},
      cancelSource: null,
      headers: [
        {
          text: "Id",
          align: "left",
          value: "id",
        },
        { text: "Първо име", value: "firstName" },
        { text: "Фамилия", value: "lastName" },
        { text: "Имейл", value: "email" },
        { text: "Роля", value: "role" },
        { text: "Активен", value: "active" },
        {
          align: "right",
          text: "Опции",
          value: "actions",
          sortable: false,
          width: "10%",
        },
      ],
    };
  },
  components: {
    FormSelect,
    SearchTools,
    ResponsiveDataTable,
    ResponsiveFilterSheet,
  },
  watch: {},
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: this.pageTitle, route: { name: this.listRouteName } },
      { title: this.$t("LABELS.LIST") },
    ]);
  },
  computed: {
    ...mapGetters(["getUsers", "isLoadingUser"]),
    pageTitle() {
      return this.mechanicsOnly
        ? this.$t("MENU.ITEM.SERVICE.MECHANICS")
        : this.$t("MENU.ITEM.SETTINGS.USERS");
    },
    listRouteName() {
      return this.mechanicsOnly ? "list-mechanics" : "list-users";
    },
    fetchAction() {
      return this.mechanicsOnly ? FETCH_MECHANICS : FETCH_USERS;
    },
    canEditUsers() {
      return !this.mechanicsOnly && this.checkIfUserHasPermission(["users-update"]);
    },
    params() {
      return {
        ...this.options,
        query: this.searchQuery
      };
    },
    tableHeaders() {
      return this.mechanicsOnly
        ? this.headers.filter(header => header.value !== "actions")
        : this.headers;
    },
    mobileSummaryFields() {
      return [
        {
          key: "name",
          label: this.$t("MENU.ITEM.SERVICE.MECHANICS"),
          primary: true,
          value: item => `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.email || "-"
        },
        {
          key: "email",
          label: "Имейл"
        },
        {
          key: "active",
          label: "Активен",
          slot: "active"
        }
      ];
    },
    mobileDetailFields() {
      return [
        {
          key: "role",
          label: "Роля"
        },
        {
          key: "id",
          label: "Id"
        }
      ];
    },
  },
  methods: {
    cancelRequest() {
      //Axios cancelSource to stop current search if new value is entered
      if (this.cancelSource) {
        this.cancelSource.cancel("Start new search, stop active search");
      }
    },
    doSearch() {
      let vm = this;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    doClear() {
      let vm = this;
      vm.searchQuery = "";
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    getDataFromApi() {
      let vm = this;
      // vm.tableLoading = true;
      //copy current params to modify
      let params = this.params;

      params.length = params.itemsPerPage; //set how many records to fecth per page
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1); //set offset
      // console.log(this.params);
      let apiParams = vm.transformUrlParams(params);
      // console.log(apiParams);
      vm.$store
        .dispatch(this.fetchAction, apiParams)
        .then((data) => {
          vm.$nextTick(function() {
            vm.items = data.data.items;
            vm.totalItems = data.data.totalItemsCount;

            if (vm.isPageLoading == true) {
              vm.isPageLoading = false;
            }
          });
        })
        .catch((response) => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Nout Found",
            });
            this.$router.push({ name: this.listRouteName });
          }
        });
    },
    transformUrlParams(params) {
      let apiParams =
        "?" +
        Object.keys(params)
          .map((key) => key + "=" + params[key])
          .join("&");
      return apiParams;
    },
    getColor(active) {
      if (active == false || active == 0) return "red";
      else return "primary";
    },
    textIsActive(active) {
      if (active == false || active == 0) return "Not Active";
      else return "Active";
    },
    iconIsActive(active) {
      if (active == false || active == 0) return "mdi-close-circle-outline";
      else return "mdi-checkbox-marked-circle-outline";
    },
  },
};
</script>

<style scoped>
.user-list__status {
  color: #3f4254;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1;
}

.user-list__status-dot {
  height: 8px;
  min-height: 8px;
  min-width: 8px;
  width: 8px;
}
</style>
